import qs from 'qs';
import _ from 'lodash';

import { parseFilterModalQuery } from '../../../utils/query.utils';
import {
  describeConditions,
  type FilterCondition,
  type FilterConfig,
} from './condition-summary';
import { type ChipRecord } from './chip-management';
import {
  columnLabelOf,
  partsOfColumn,
  withCombinedColumns,
} from './combined-columns';

/**
 * The bucket a chip whose stored query cannot be read falls into.
 *
 * <p>A sentinel rather than the empty string, so a chip that parses to no columns and a chip that
 * failed to parse at all land in the same visible group instead of one of them disappearing.
 */
export const OTHER_GROUP_KEY = '__other__';

export const OTHER_GROUP_LABEL = 'Other';

export interface ChipGroup<T> {
  readonly key: string;
  readonly label: string;
  readonly chips: readonly T[];
}

/**
 * A chip's stored query as conditions, whichever prefix it is stored under.
 *
 * <p>The same chip reaches this module spelled two ways: the manage panel reads the record
 * straight from the service, where it is `filter[...]`, and the listing's chip row is handed the
 * copy `useFilterState` rewrites to `f[...]`. Accepting both is cheaper than making either caller
 * normalise, and a chip that matches neither is reported as unparseable rather than guessed at.
 */
export const parseChipFilter = (
  filter: unknown
): readonly FilterCondition[] | null => {
  if (typeof filter !== 'string' || filter.trim() === '') {
    return null;
  }

  try {
    const parsed = qs.parse(filter) as Record<string, unknown>;
    const branch = parsed?.f ?? parsed?.filter;

    if (!branch || typeof branch !== 'object') {
      return null;
    }

    const conditions = parseFilterModalQuery({ f: branch });

    return conditions.length > 0 ? conditions : null;
  } catch {
    return null;
  }
};

/** Every column a chip's stored query touches, or null when the query cannot be read. */
export const chipColumns = (filter: unknown): readonly string[] | null => {
  const conditions = parseChipFilter(filter);

  if (!conditions) {
    return null;
  }

  const columns = conditions
    .map((condition) => condition?.column ?? '')
    .filter(Boolean);

  return columns.length > 0 ? columns : null;
};

/**
 * The column names a chip shares with others.
 *
 * <p>A column name can itself be a comma list -- a real chip on the quotes listing reads
 * `filter[isRenewal,isMigration][bo][0]=1`, where the comma means "either of these" and the whole
 * string is the column. Split for the purposes of sharing, because the chip that filters
 * `isRenewal` on its own is asking about the same field and belongs beside it.
 */
export const sharedColumnsOf = (
  columns: readonly string[] | null | undefined
): readonly string[] =>
  _.uniq(_.flatMap(columns ?? [], (column) => partsOfColumn(column)));

/** A group's heading -- every column its chips touch, in the modal's own words. */
export const groupLabelFor = (
  columns: readonly string[],
  config: FilterConfig = {}
): string => {
  if (columns.length === 0) {
    return OTHER_GROUP_LABEL;
  }

  return columns.map((column) => columnLabelOf(column, config)).join(' / ');
};

/**
 * Chips bucketed into sets where picking one means not picking the others.
 *
 * <p>Two chips belong together when they filter on a column in common -- not when their columns
 * match exactly. On the quotes listing "New Business" filters `quoteNumber`, `isRenewal` and
 * `isMigration`; "Endorsements" filters `quoteNumber`; "Renewals" filters `isRenewal,isMigration`.
 * All three answer the same question and an exact-match rule put each in a group of its own, which
 * is a divider between every chip and no grouping at all.
 *
 * <p>Sharing is transitive, so the three above form one group through "New Business" even though
 * "Endorsements" and "Renewals" have no column between them. That is the intent: they are
 * alternatives, and the row is a set of alternatives.
 *
 * <p>Groups come back in the order their first chip appeared, and chips keep the order they
 * arrived in. Sorting either would be a second opinion on order: the manage panel already sorts by
 * name before this runs, and the listing's chip row has no order of its own to lose.
 *
 * <p>The unreadable ones are always last -- the group nobody is looking for, and putting them
 * first would push the working chips down.
 */
export const groupChips = <T>(
  chips: readonly T[],
  columnsFor: (chip: T) => readonly string[] | null | undefined,
  config: FilterConfig = {}
): readonly ChipGroup<T>[] => {
  const list = chips ?? [];
  const columnsPerChip = list.map((chip) => sharedColumnsOf(columnsFor(chip)));

  const parent = list.map((_unused, index) => index);
  const rootOf = (index: number): number => {
    let node = index;
    while (parent[node] !== node) {
      parent[node] = parent[parent[node]];
      node = parent[node];
    }
    return node;
  };
  const join = (a: number, b: number) => {
    const rootA = rootOf(a);
    const rootB = rootOf(b);
    if (rootA !== rootB) {
      parent[rootB] = rootA;
    }
  };

  const firstHolderOf = new Map<string, number>();
  columnsPerChip.forEach((columns, index) => {
    columns.forEach((column) => {
      const holder = firstHolderOf.get(column);
      if (holder === undefined) {
        firstHolderOf.set(column, index);
        return;
      }
      join(holder, index);
    });
  });

  /** One bucket for everything unreadable, whatever else it would have rooted to. */
  const UNREADABLE = -1;
  const order: number[] = [];
  const members = new Map<number, number[]>();

  list.forEach((_unused, index) => {
    const root = columnsPerChip[index].length > 0 ? rootOf(index) : UNREADABLE;
    const bucket = members.get(root);

    if (bucket) {
      bucket.push(index);
      return;
    }

    members.set(root, [index]);
    order.push(root);
  });

  const groups = order.map((root) => {
    const indexes = members.get(root) ?? [];
    const columns = _.uniq(
      _.flatMap(indexes, (index) => columnsPerChip[index])
    );

    return {
      key:
        columns.length > 0 ? columns.slice().sort().join('+') : OTHER_GROUP_KEY,
      label: groupLabelFor(columns, config),
      chips: indexes.map((index) => list[index]),
    };
  });

  return groups
    .filter((group) => group.key !== OTHER_GROUP_KEY)
    .concat(groups.filter((group) => group.key === OTHER_GROUP_KEY));
};

/** The manage panel's records, grouped. */
export const groupChipRecords = (
  chips: readonly ChipRecord[],
  config: FilterConfig = {}
): readonly ChipGroup<ChipRecord>[] =>
  groupChips(chips, (chip) => chipColumns(chip?.filter), config);

/**
 * What a chip does, in the words its conditions were chosen with.
 *
 * <p>The stored query is what was shown here before -- `filter[status][in][0]=PAID` -- which tells
 * an administrator deciding whether to delete a chip almost nothing. The raw string stays as the
 * row's tooltip, and is still what is shown when the query cannot be read, because then it is the
 * only true thing left to say about it.
 */
export const chipSummary = (
  filter: unknown,
  config: FilterConfig = {}
): string => {
  const conditions = parseChipFilter(filter);

  if (!conditions) {
    return typeof filter === 'string' ? filter : '';
  }

  const described = describeConditions(
    conditions,
    withCombinedColumns(
      config,
      conditions.map((condition) => condition?.column ?? '')
    )
  );

  return described || (typeof filter === 'string' ? filter : '');
};
